import { ChangeDetectionStrategy, Component, input, output, signal, computed, OnInit } from '@angular/core';

/**
 * Collapsible content section using native HTML details/summary elements.
 * Supports exclusive accordion behavior when groupName is provided.
 *
 * @example
 * <ui-accordion
 *   heading="Section Title"
 *   [openOnRender]="false"
 *   (toggle)="onToggle($event)">
 *   Content goes here
 * </ui-accordion>
 */
@Component({
  selector: 'ui-accordion',
  imports: [],
  templateUrl: './accordion.component.html',
  styleUrls: ['../../_styles/index.scss', './accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionComponent {
  heading = input<string>(); // Text displayed in the accordion header
  openHeading = input<string>(); // Optional, shown when accordion is open
  openOnRender = input<boolean>(false); // State on initial render
  content = input<string>(); // Optional - use content projection for complex content */
  groupName = signal<string>(''); // Enables exclusive accordion behavior
  isOpen = signal<boolean>(false); // Tracks current open/closed state
  toggle = output<boolean>(); // Emits when accordion state changes

  /**
   * Computed heading that switches between default and open states
   * Uses fallback pattern for better readability
   */
  currentHeading = computed(() => {
    const isCurrentlyOpen = this.isOpen();
    const alternateHeading = this.openHeading();

    return isCurrentlyOpen && alternateHeading ? alternateHeading : this.heading();
  });

  /**
   * Handles accordion toggle events from the native details element
   * Synchronizes internal state with DOM state
   */
  onClick(details: HTMLDetailsElement): void {
    if (!details) return;

    const newState = details.open;
    this.isOpen.set(newState);
    this.toggle.emit(newState);
  }
}
